import { observer, useLocalObservable } from 'mobx-react';
import { useMemo, useCallback, useEffect } from 'react';
import { useStore } from '../../store/store';
import InputWrapper from './components/InputWrapper';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../buttons/IconButton';

const MultiSelectRow = observer(
  ({
    field,
    value = [],
    values,
    valuesKey = '',
    setFieldValue,
    showOnSubmitErrorState = false,
    messages = [],
    disabled = false,
    hideErrorMessages = false,
    hideRequiredLabelSymbol = false,
    hideLabel = false,
    inputWrap = { enable: false, className: '' },
    wrapClassname = '',
    forceSingleSelect = false,
    selectedIcon = ICON_BUTTON_ICONS.CHECK_SQUARE_FILL,
    notSelectedIcon = ICON_BUTTON_ICONS.SQUARE
  }) => {
    const { utilsStore } = useStore();
    const state = useLocalObservable(() => ({
      rendered: false,
      setRendered: (value = false) => (state.rendered = value),
      dropdownToggled: false,
      setDropdownToggled: (isToggled = false) => (state.dropdownToggled = isToggled)
    }));

    const items = useMemo(() => {
      return (
        Array.isArray(values)
          ? values
          : Array.isArray(field?.VALUES)
            ? field.VALUES
            : valuesKey || field.VALUES_KEY
              ? utilsStore?.options?.[valuesKey || field.VALUES_KEY] || []
              : []
      ).map((f) => ({
        name: f.name,
        id: f.value
      }));
    }, [utilsStore.options, field, valuesKey, values]);

    const selected = useMemo(() => {
      return items.filter((opt) => value.some((val) => val == opt.id));
    }, [items, value]);

    useEffect(() => {
      state.setRendered(true);
      return () => {
        state.setRendered();
      };
    }, [state]);

    const props = useMemo(() => {
      const labelProps = {};

      if (field.LABEL_CLASSNAME) {
        labelProps.className = field.LABEL_CLASSNAME;
      }

      return { labelProps };
    }, [field, value]);

    const onOptionClick = useCallback(
      (option) => {
        let newSelections = selected.slice();
        const foundInSelectedIndex = newSelections.findIndex((opt) => opt.id === option.id);
        if (foundInSelectedIndex !== -1) {
          newSelections.splice(foundInSelectedIndex, 1);
        } else {
          if (forceSingleSelect) {
            newSelections = [option];
          } else {
            newSelections.push(option);
          }
        }

        setFieldValue(
          field,
          newSelections.map((i) => i.id)
        );
      },
      [setFieldValue, field, selected, forceSingleSelect]
    );

    const { validationState, onSubmitErrorMessages } = useMemo(() => {
      let validationState = '';
      const onSubmitErrorMessages = showOnSubmitErrorState
        ? messages.filter((m) => m.field === field.NAME && !m.isValid)
        : [];
      if (showOnSubmitErrorState && onSubmitErrorMessages.length) {
        validationState = 'error';
      }

      return { validationState, onSubmitErrorMessages };
    }, [showOnSubmitErrorState, messages]);

    const withWrap = inputWrap.enable;

    const view = (
      <div
        className={
          (wrapClassname || field.WRAP_CLASSNAME || 'form-group') +
          ' select-form-group' +
          (state.dropdownToggled ? ' focused' : '')
        }
        {...{ 'validation-state': validationState }}>
        {!!field.LABEL && !hideLabel && (
          <label htmlFor={field.ID} {...props.labelProps}>
            {field.LABEL}
            {field.REQUIRED && !hideRequiredLabelSymbol && !field.HIDE_REQUIRED_LABEL_SYMBOL && (
              <font className="red-asterisk">*</font>
            )}
            {!!field.LABEL_DESCRIPTION && <span>{field.LABEL_DESCRIPTION}</span>}
          </label>
        )}
        <div className="input-error-group">
          <div className={`input-container multiselectrow ${disabled ? 'disabled' : ''}`}>
            {!items.length && 'No options available'}
            {items.map((item) => {
              const isSelected = !!selected.find((opt) => opt.id == item.id);
              const type = isSelected ? ICON_BUTTON_TYPES.BLUE : ICON_BUTTON_TYPES.DEFAULT;
              const icon = isSelected ? selectedIcon : notSelectedIcon;
              return (
                <IconButton
                  key={item.id}
                  innerText={item.name}
                  withFill={false}
                  type={type}
                  onClick={() => onOptionClick(item)}
                  icon={icon}
                />
              );
            })}
          </div>
          {!hideErrorMessages &&
            onSubmitErrorMessages
              .filter(({ msg }) => msg.length)
              .map(({ msg }, idx) => (
                <div
                  key={idx}
                  className={
                    field.ON_SUBMIT_ERROR_STATE_ERROR_MESSAGE_CLASSNAME || 'error-msg-input'
                  }>
                  {msg}
                </div>
              ))}
        </div>
      </div>
    );

    return withWrap ? (
      <InputWrapper className={inputWrap.className || ''}>{view}</InputWrapper>
    ) : (
      view
    );
  }
);

export default MultiSelectRow;
